import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Typography from "../../Utility/Typography";

/** Match existing dashboard legend */
const SEGMENT_COLORS = {
  todo: "#f13a88",
  inProgress: "rgb(85, 127, 241)",
  done: "#3ab0be",
};

function normalizeSegment(raw) {
  if (!raw || typeof raw !== "object") return { count: 0, percent: 0 };
  return {
    count: Math.max(0, Number(raw.count) || 0),
    percent: Math.min(100, Math.max(0, Number(raw.percent) || 0)),
  };
}

function formatPercentDisplay(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "0";
  const rounded = Math.round(x * 10) / 10;
  if (Number.isInteger(rounded)) return String(rounded);
  return rounded.toFixed(1);
}

/**
 * Map API epic (client dashboard) to UI model.
 * Supports `progressByCategory` (camelCase) or `progress_by_category` (snake_case).
 */
function mapEpicFromApi(e) {
  const raw = e && typeof e === "object" ? e : {};
  const title = String(raw.title ?? raw.name ?? "").trim();
  const key = String(raw.key ?? raw.itemKey ?? "").trim();
  const id = raw.id != null ? String(raw.id) : key || title;
  const pbc = raw.progressByCategory ?? raw.progress_by_category;
  const total = pbc != null && typeof pbc === "object" ? Math.max(0, Number(pbc.total) || 0) : 0;
  const todo = normalizeSegment(pbc?.todo ?? pbc?.to_do);
  const inProgress = normalizeSegment(pbc?.inProgress ?? pbc?.in_progress);
  const done = normalizeSegment(pbc?.done);

  const overallPercent = Math.min(
    100,
    Math.max(0, Number(raw.percent ?? (total > 0 ? done.percent : raw.progress) ?? 0) || 0)
  );

  const hasBreakdown =
    total > 0 &&
    (todo.count > 0 ||
      inProgress.count > 0 ||
      done.count > 0 ||
      todo.percent + inProgress.percent + done.percent > 0);

  let todoW = todo.percent;
  let inProgressW = inProgress.percent;
  let doneW = done.percent;
  if (hasBreakdown) {
    const sum = todoW + inProgressW + doneW;
    if (sum > 0 && Math.abs(sum - 100) > 1) {
      todoW = total > 0 ? (todo.count / total) * 100 : 0;
      inProgressW = total > 0 ? (inProgress.count / total) * 100 : 0;
      doneW = total > 0 ? (done.count / total) * 100 : 0;
    } else if (sum > 0 && Math.abs(sum - 100) <= 1) {
      /* use API percents as widths */
    } else if (total > 0) {
      todoW = (todo.count / total) * 100;
      inProgressW = (inProgress.count / total) * 100;
      doneW = (done.count / total) * 100;
    }
  }

  return {
    id,
    key,
    title,
    overallPercent,
    total,
    todo: { ...todo, width: todoW },
    inProgress: { ...inProgress, width: inProgressW },
    done: { ...done, width: doneW },
    hasBreakdown,
  };
}

export default function EpicProgress({ epics: epicsProp } = {}) {
  const navigate = useNavigate();

  const epics = useMemo(() => {
    if (!Array.isArray(epicsProp) || epicsProp.length === 0) return [];
    return epicsProp.map(mapEpicFromApi).filter((e) => e.title !== "");
  }, [epicsProp]);

  const handleViewAllItems = () => {
    navigate("?tab=issues");
  };

  if (epics.length === 0) {
    return (
      <div className="py-10 text-center">
        <Typography variant="text2" className="text-gray-500">
          No data
        </Typography>
      </div>
    );
  }

  return (
    <div>
      <Typography variant="span2" className="text-sm text-gray-600" component="p">
        See how each epic splits across to do, in progress, and done (bar width = share of items).
        <Typography
          variant="span2"
          className="text-blue-600 cursor-pointer ml-1"
          component="span"
          onClick={handleViewAllItems}
        >
          View all epics
        </Typography>
      </Typography>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 mb-3">
        <div className="flex items-center gap-1">
          <Typography
            variant="span2"
            className="w-3 h-3 inline-block rounded-sm shrink-0"
            style={{ backgroundColor: SEGMENT_COLORS.todo }}
            component="span"
          />
          To Do
        </div>
        <div className="flex items-center gap-1">
          <Typography
            variant="span2"
            className="w-3 h-3 inline-block rounded-sm shrink-0"
            style={{ backgroundColor: SEGMENT_COLORS.inProgress }}
            component="span"
          />
          In Progress
        </div>
        <div className="flex items-center gap-1">
          <Typography
            variant="span2"
            className="w-3 h-3 inline-block rounded-sm shrink-0"
            style={{ backgroundColor: SEGMENT_COLORS.done }}
            component="span"
          />
          Done
        </div>
      </div>
      <ul className="list-none m-0 p-0 space-y-4">
        {epics.map((epic) => (
          <li key={epic.id}>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm font-medium text-gray-800 mb-1">
              <span aria-hidden>⚡</span>
              {epic.key ? (
                <Typography variant="span2" className="text-gray-500 font-normal" component="span">
                  {epic.key}
                </Typography>
              ) : null}
              <Typography variant="span2" className="text-gray-800" component="span">
                {epic.title}
              </Typography>
              <Typography
                variant="span2"
                className="text-gray-500 font-normal ml-auto"
                component="span"
              >
                {formatPercentDisplay(epic.overallPercent)}% complete
                {epic.total > 0 ? ` · ${epic.total} items` : ""}
              </Typography>
            </div>

            {epic.hasBreakdown ? (
              <div
                className="w-full h-6 rounded overflow-hidden flex bg-gray-200"
                role="img"
                aria-label={`${epic.title}: To Do ${epic.todo.count}, In Progress ${epic.inProgress.count}, Done ${epic.done.count}`}
              >
                {epic.todo.width > 0 ? (
                  <div
                    className="h-6 min-w-0 flex items-center justify-center text-[10px] font-semibold text-white leading-none px-0.5"
                    style={{
                      width: `${Math.min(100, Math.max(0, epic.todo.width))}%`,
                      backgroundColor: SEGMENT_COLORS.todo,
                    }}
                    title={`To Do: ${epic.todo.count} (${formatPercentDisplay(epic.todo.width)}%)`}
                  >
                    {epic.todo.width >= 8 ? `${formatPercentDisplay(epic.todo.width)}%` : ""}
                  </div>
                ) : null}
                {epic.inProgress.width > 0 ? (
                  <div
                    className="h-6 min-w-0 flex items-center justify-center text-[10px] font-semibold text-white leading-none px-0.5"
                    style={{
                      width: `${Math.min(100, Math.max(0, epic.inProgress.width))}%`,
                      backgroundColor: SEGMENT_COLORS.inProgress,
                    }}
                    title={`In Progress: ${epic.inProgress.count} (${formatPercentDisplay(epic.inProgress.width)}%)`}
                  >
                    {epic.inProgress.width >= 8
                      ? `${formatPercentDisplay(epic.inProgress.width)}%`
                      : ""}
                  </div>
                ) : null}
                {epic.done.width > 0 ? (
                  <div
                    className="h-6 min-w-0 flex items-center justify-center text-[10px] font-semibold text-white leading-none px-0.5"
                    style={{
                      width: `${Math.min(100, Math.max(0, epic.done.width))}%`,
                      backgroundColor: SEGMENT_COLORS.done,
                    }}
                    title={`Done: ${epic.done.count} (${formatPercentDisplay(epic.done.width)}%)`}
                  >
                    {epic.done.width >= 8 ? `${formatPercentDisplay(epic.done.width)}%` : ""}
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="relative w-full bg-gray-200 rounded h-6 overflow-hidden">
                <div
                  className="h-6 rounded transition-[width]"
                  style={{
                    width: `${Math.min(100, Math.max(0, epic.overallPercent))}%`,
                    backgroundColor: SEGMENT_COLORS.done,
                  }}
                  aria-hidden
                />
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <Typography
                    variant="span2"
                    component="span"
                    className="text-xs font-semibold text-gray-900"
                  >
                    {formatPercentDisplay(epic.overallPercent)}%
                  </Typography>
                </div>
              </div>
            )}

            {epic.hasBreakdown && epic.total > 0 ? (
              <Typography variant="span2" className="text-xs text-gray-500 mt-1 block">
                {epic.todo.count} to do · {epic.inProgress.count} in progress · {epic.done.count}{" "}
                done
              </Typography>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
